import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Truck, CheckCircle, Warning, FileText, Plus, X, Printer, Receipt, FilePdf, FileXls } from '@phosphor-icons/react';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';
import { useRealtime } from '../contexts/RealtimeContext';
import SmartPicker from '../components/SmartPicker';
import WorkflowActions from '../components/WorkflowActions';
import DraftRestoreBanner from '../components/DraftRestoreBanner';
import { useWorkflowDraft } from '../hooks/useWorkflowDraft';
import { useHighlight } from '../hooks/useHighlight';

const VEHICLE_TYPES = ['Regular', 'Over Dimensional Cargo', 'Multi Axle Vehicle', 'Tanker', 'Trailer'];

function printDispatchNote(n) {
  const eway = n.ewaybill_no ? `
<div class="section">
  <div class="section-title">e-Way Bill Details</div>
  <div class="grid">
    <div class="item"><div class="lbl">e-Way Bill No</div><div class="val">${n.ewaybill_no}</div></div>
    <div class="item"><div class="lbl">Bill Date</div><div>${n.ewaybill_date || '—'}</div></div>
    <div class="item"><div class="lbl">Transporter ID</div><div>${n.transporter_id || '—'}</div></div>
    <div class="item"><div class="lbl">Vehicle Type</div><div>${n.vehicle_type || '—'}</div></div>
    <div class="item"><div class="lbl">Distance (km)</div><div>${n.distance_km || '—'}</div></div>
  </div>
</div>` : '';
  const win = window.open('', '_blank', 'width=820,height=960');
  win.document.write(`<!DOCTYPE html><html><head><title>Dispatch Note ${n.dispatch_no}</title>
<style>
  body{font-family:monospace;padding:40px;color:#111;font-size:13px}
  .hdr{text-align:center;border-bottom:3px solid #111;padding-bottom:16px;margin-bottom:24px}
  h1{font-size:24px;letter-spacing:.2em;margin:0;text-transform:uppercase}
  h2{font-size:13px;margin:4px 0;color:#555;text-transform:uppercase}
  .section{margin-bottom:20px}
  .section-title{font-size:10px;color:#888;text-transform:uppercase;letter-spacing:.1em;margin-bottom:8px;border-bottom:1px solid #eee;padding-bottom:4px}
  .row{display:flex;justify-content:space-between;gap:24px;margin-bottom:20px}
  .lbl{font-size:10px;color:#888;text-transform:uppercase;letter-spacing:.1em;margin-bottom:4px}
  .val{font-size:13px;font-weight:700}
  .grid{display:grid;grid-template-columns:1fr 1fr 1fr;gap:16px}
  .item{border:1px solid #ddd;padding:12px}
  .chip{display:inline-block;background:#000;color:#fff;padding:2px 10px;font-size:10px;text-transform:uppercase;letter-spacing:.08em}
  table{width:100%;border-collapse:collapse;margin:16px 0}
  th,td{border:1px solid #ccc;padding:8px 12px;font-size:12px}
  th{background:#f5f5f5;font-size:11px;text-transform:uppercase}
  .footer{margin-top:40px;font-size:10px;color:#aaa;border-top:1px solid #ddd;padding-top:12px;text-align:center}
  @media print{@page{margin:18mm}}
</style></head><body>
<div class="hdr"><h1>ARIA FORGE ERP</h1><h2>Dispatch Note / Delivery Challan</h2></div>
<div class="row">
  <div>
    <div class="lbl">Dispatch No</div><div class="val">${n.dispatch_no}</div>
    <div class="lbl" style="margin-top:8px">Order Ref</div><div>${n.order_no || '—'}</div>
    <div class="lbl" style="margin-top:8px">Status</div><span class="chip">${n.status?.toUpperCase()}</span>
  </div>
  <div>
    <div class="lbl">Deliver To</div><div class="val">${n.customer_name}</div>
    <div class="lbl" style="margin-top:8px">Scheduled Date</div>
    <div>${n.scheduled_date ? new Date(n.scheduled_date).toLocaleDateString('en-IN') : '—'}</div>
  </div>
</div>
<div class="section">
  <div class="section-title">Shipment Details</div>
  <div class="grid">
    <div class="item"><div class="lbl">Product</div><div>${n.product_name || '—'}</div></div>
    <div class="item"><div class="lbl">Quantity</div><div class="val">${(n.quantity || 0).toLocaleString()} units</div></div>
    <div class="item"><div class="lbl">Invoice No</div><div>${n.invoice_no || '—'}</div></div>
    <div class="item"><div class="lbl">Transporter</div><div>${n.transporter || '—'}</div></div>
    <div class="item"><div class="lbl">Vehicle No</div><div>${n.vehicle_no || '—'}</div></div>
    <div class="item"><div class="lbl">Invoice Amount</div><div>₹${((n.invoice_amount || 0) / 1000).toFixed(0)}K</div></div>
  </div>
</div>
${eway}
<table>
  <tr><th>Document</th><th>Status</th></tr>
  <tr><td>Invoice / Bill</td><td>${n.documents_complete ? 'YES' : 'NO'}</td></tr>
  <tr><td>e-Way Bill</td><td>${n.ewaybill_no ? `YES — ${n.ewaybill_no}` : 'NOT ATTACHED'}</td></tr>
  <tr><td>QC Released</td><td>${n.qc_released ? 'YES' : 'PENDING'}</td></tr>
</table>
<div class="footer">Generated by ARIA FORGE ERP &nbsp;|&nbsp; ${new Date().toLocaleString('en-IN')}</div>
<script>window.onload=()=>window.print()</script>
</body></html>`);
  win.document.close();
}

function StatusChip({ status }) {
  const map = { pending: 'chip-neutral', ready: 'chip-warning', dispatched: 'chip-active' };
  return <span className={map[status] || 'chip-neutral'}>{status?.toUpperCase()}</span>;
}

function DocCheck({ ok, label }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 0' }}>
      {ok ? <CheckCircle size={12} color="hsl(197,100%,48%)" weight="fill" />
           : <Warning size={12} color="hsl(0,84%,60%)" weight="fill" />}
      <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10,
        color: ok ? 'hsl(0,0%,70%)' : 'hsl(0,84%,60%)' }}>{label}</span>
    </div>
  );
}

const INP = {
  width: '100%', background: 'hsl(0,0%,8%)', border: '1px solid hsl(0,0%,16%)',
  padding: '8px 10px', color: 'hsl(0,0%,85%)', fontFamily: "'JetBrains Mono', monospace",
  fontSize: 11, outline: 'none', boxSizing: 'border-box',
};
const LBL = {
  display: 'block', fontFamily: "'JetBrains Mono', monospace", fontSize: 9,
  color: 'hsl(0,0%,45%)', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 4,
};

export default function Dispatch() {
  const [notes, setNotes] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showEway, setShowEway] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [deductToast, setDeductToast] = useState(null);
  const [ewayForm, setEwayForm] = useState({ ewaybill_no: '', ewaybill_date: '', transporter_id: '', vehicle_type: 'Regular', distance_km: '' });
  const [form, setForm] = useState({
    order_no: '', customer_name: '', product_name: '', product_sku: '', quantity: '',
    scheduled_date: '', transporter: '', carrier_id: '', vehicle_no: '', invoice_no: '', invoice_amount: '',
  });

  const { user } = useAuth();
  const navigate = useNavigate();
  const { notifications } = useRealtime();
  const highlightId = useHighlight();
  const { prefill, hasDraft, restoreDraft, discardDraft, clearDraft } = useWorkflowDraft('dispatch', 'create_note');

  // Apply prefill when it arrives (from action button cross-tab handoff)
  useEffect(() => {
    if (!prefill) return;
    setForm(prev => ({
      ...prev,
      order_no: prefill.order_no || prev.order_no,
      customer_name: prefill.customer_name || prev.customer_name,
      product_name: prefill.product_name || prev.product_name,
      product_sku: prefill.product_sku || prev.product_sku,
      quantity: String(prefill.quantity || prev.quantity),
    }));
  }, [prefill]);

  const load = () => api.get('/dispatch').then(r => { setNotes(r.data); setLoading(false); }).catch(() => setLoading(false));
  useEffect(() => { load(); }, []);

  // Refresh dispatch list when a dispatch-updated realtime event fires (e.g. after invoice creation)
  useEffect(() => {
    const latest = notifications[0];
    if (latest?.event === 'dispatch-updated') {
      load();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notifications]);

  const handleConfirm = async (id) => {
    try {
      const res = await api.put(`/dispatch/${id}/confirm`);
      const deductions = res.data?.stock_deductions || [];
      if (deductions.length > 0) {
        const msgs = deductions.map(d => `${d.item} -${d.deducted}`).join(', ');
        setDeductToast(`FG Stock deducted: ${msgs}`);
        setTimeout(() => setDeductToast(null), 5000);
      }
      const r = await api.get('/dispatch');
      setNotes(r.data);
      if (selected?.id === id) setSelected(r.data.find(n => n.id === id) || null);
    } catch (e) { console.error(e); }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      await api.post('/dispatch', { ...form, quantity: parseInt(form.quantity) || 0, invoice_amount: parseFloat(form.invoice_amount) || 0 });
      clearDraft();
      await load(); setShowAdd(false);
    } catch (e) { console.error(e); }
  };

  const handleEWaySubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await api.put(`/dispatch/${selected.id}/ewaybill`, { ...ewayForm, distance_km: parseInt(ewayForm.distance_km) || 0 });
      setSelected(res.data);
      setNotes(prev => prev.map(n => n.id === selected.id ? res.data : n));
      setShowEway(false);
    } catch (e) { console.error(e); }
  };

  const handleDownloadPDF = async (n) => {
    if (!n) return;
    setPdfLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('aria_token');
      const resp = await fetch(`${backendUrl}/api/dispatch/${n.id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error('PDF generation failed');
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `Challan_${n.dispatch_no}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { console.error(err); alert('PDF generation failed'); }
    finally { setPdfLoading(false); }
  };

  const openEway = (n) => {
    setEwayForm({
      ewaybill_no: n.ewaybill_no || '', ewaybill_date: n.ewaybill_date || new Date().toISOString().split('T')[0],
      transporter_id: n.transporter_id || '', vehicle_type: n.vehicle_type || 'Regular',
      distance_km: n.distance_km || '',
    });
    setShowEway(true);
  };

  const stats = {
    pending: notes.filter(n => n.status === 'pending').length,
    ready: notes.filter(n => n.status === 'ready').length,
    dispatched: notes.filter(n => n.status === 'dispatched').length,
    noEway: notes.filter(n => !n.ewaybill_no && n.status !== 'dispatched').length,
  };

  return (
    <div data-testid="dispatch-page">
      {/* FG Stock Deduction Toast */}
      {deductToast && (
        <div data-testid="deduct-stock-toast"
          style={{ position: 'fixed', top: 20, right: 20, zIndex: 9999,
            padding: '10px 18px', background: 'hsl(43,80%,10%)',
            border: '1px solid hsl(43,80%,35%)',
            color: 'hsl(43,96%,60%)', fontFamily: "'JetBrains Mono', monospace",
            fontSize: 11, letterSpacing: '0.05em', boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
            maxWidth: 380 }}>
          {deductToast}
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 22, fontWeight: 700, color: 'hsl(0,0%,98%)', textTransform: 'uppercase', marginBottom: 4 }}>Dispatch</h1>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: 'hsl(0,0%,45%)', textTransform: 'uppercase', letterSpacing: '0.12em' }}>Shipment Queue & e-Way Bill Management</div>
        </div>
        <button data-testid="add-dispatch-btn" onClick={() => setShowAdd(true)} className="btn-primary" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Plus size={14} weight="bold" /> NEW DISPATCH
        </button>
      </div>

      <div className="metrics-row" style={{ marginBottom: 20 }}>
        {[
          { label: 'Pending', val: stats.pending, color: 'hsl(0,0%,55%)', icon: Truck },
          { label: 'Ready to Ship', val: stats.ready, color: 'hsl(43,96%,56%)', icon: Truck },
          { label: 'Dispatched', val: stats.dispatched, color: 'hsl(197,100%,48%)', icon: CheckCircle },
          { label: 'No e-Way Bill', val: stats.noEway, color: 'hsl(0,84%,60%)', icon: Warning },
        ].map(s => (
          <div key={s.label} data-testid={`dispatch-stat-${s.label.toLowerCase().replace(/ /g, '-')}`}
            style={{ flex: 1, background: 'hsl(0,0%,5%)', border: '1px solid hsl(0,0%,14%)', padding: '14px 16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'hsl(0,0%,45%)', textTransform: 'uppercase', letterSpacing: '0.12em' }}>{s.label}</div>
              <s.icon size={14} color={s.color} />
            </div>
            <div style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 26, fontWeight: 700, color: s.color }}>{s.val}</div>
          </div>
        ))}
      </div>

      <div className="split-view" style={{ gap: 1, height: 'calc(100vh - 280px)' }}>
        <div className="split-left" style={{ width: 380, background: 'hsl(0,0%,4%)', border: '1px solid hsl(0,0%,14%)', overflowY: 'auto' }}>
          <div style={{ padding: '10px 14px', borderBottom: '1px solid hsl(0,0%,12%)', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: 'hsl(0,0%,45%)', textTransform: 'uppercase', letterSpacing: '0.12em' }}>
            All Dispatches ({notes.length})
          </div>
          {loading ? <div style={{ padding: 20, color: 'hsl(0,0%,40%)', fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }}>Loading...</div>
          : notes.map(n => {
            const isHighlighted = highlightId && (n.id === highlightId || n.dispatch_no === highlightId);
            return (
              <div key={n.id} data-testid={`dispatch-item-${n.dispatch_no}`}
                onClick={() => setSelected(n)}
                style={{ padding: '12px 14px', borderBottom: '1px solid hsl(0,0%,10%)', cursor: 'pointer',
                  background: selected?.id === n.id ? 'hsl(0,0%,7%)' : 'transparent',
                  borderLeft: !n.ewaybill_no && n.status !== 'dispatched' ? '3px solid hsl(0,84%,60%)' :
                    n.status === 'ready' ? '3px solid hsl(43,96%,56%)' :
                    n.status === 'dispatched' ? '3px solid hsl(197,100%,48%)' : '3px solid transparent',
                  transition: 'background 0.1s',
                  ...(isHighlighted ? { border: '1px solid hsl(75,100%,50%)' } : {}) }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: 'hsl(197,100%,48%)', fontWeight: 700, marginBottom: 2 }}>{n.dispatch_no}</div>
                    <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: 'hsl(0,0%,75%)', marginBottom: 2 }}>{n.customer_name}</div>
                    <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: 'hsl(0,0%,50%)', marginBottom: 4 }}>{n.product_name}</div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'hsl(0,0%,40%)' }}>
                        {n.quantity?.toLocaleString()} units
                      </span>
                      {n.ewaybill_no && <span style={{ fontSize: 9, background: 'hsl(142,60%,20%)', color: 'hsl(142,70%,55%)', padding: '1px 6px', fontFamily: "'JetBrains Mono', monospace" }}>eWB</span>}
                    </div>
                  </div>
                  <StatusChip status={n.status} />
                  <WorkflowActions
                    recordType="dispatch_note"
                    record={n}
                    userRole={user?.role}
                  />
                </div>
              </div>
            );
          })}
        </div>

        <div className="split-right" style={{ flex: 1, background: 'hsl(0,0%,4%)', border: '1px solid hsl(0,0%,14%)', overflowY: 'auto' }}>
          {!selected ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'hsl(0,0%,30%)', gap: 12 }}>
              <Truck size={40} />
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.15em' }}>Select a dispatch note</div>
            </div>
          ) : (
            <div data-testid="dispatch-detail" style={{ padding: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, paddingBottom: 16, borderBottom: '1px solid hsl(0,0%,12%)' }}>
                <div>
                  <h2 style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 18, fontWeight: 700, color: 'hsl(0,0%,95%)', textTransform: 'uppercase', marginBottom: 4 }}>{selected.dispatch_no}</h2>
                  <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: 'hsl(0,0%,55%)' }}>{selected.order_no}</div>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <StatusChip status={selected.status} />
                  <button onClick={() => openEway(selected)} data-testid="eway-btn"
                    className="btn-ghost" style={{ padding: '5px 10px', fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <FileText size={12} />{selected.ewaybill_no ? 'Edit e-Way Bill' : 'Add e-Way Bill'}
                  </button>
                  <button onClick={() => printDispatchNote(selected)} data-testid="print-dispatch-btn"
                    className="btn-ghost" style={{ padding: '5px 10px', fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Printer size={12} />Print Note
                  </button>
                  <button onClick={() => handleDownloadPDF(selected)} data-testid="download-pdf-btn"
                    disabled={pdfLoading}
                    className="btn-ghost" style={{ padding: '5px 10px', fontSize: 11, display: 'flex', alignItems: 'center', gap: 4, color: pdfLoading ? 'hsl(0,0%,35%)' : 'hsl(0,84%,70%)' }}>
                    <FilePdf size={12} />{pdfLoading ? 'Generating...' : 'PDF Challan'}
                  </button>
                  {selected.status !== 'dispatched' && (
                    <button data-testid="confirm-dispatch-btn" onClick={() => handleConfirm(selected.id)}
                      className="btn-primary" style={{ fontSize: 11 }}>
                      CONFIRM DISPATCH
                    </button>
                  )}
                  {!selected.invoice_no ? (
                    <button
                      data-testid="generate-invoice-btn"
                      onClick={() => navigate(`/finance/invoices/new?from_dispatch=${selected.id}`)}
                      style={{ padding: '5px 10px', fontSize: 11, display: 'flex', alignItems: 'center', gap: 4,
                        background: 'none', border: '1px solid hsl(75,100%,50%)', cursor: 'pointer',
                        color: 'hsl(75,100%,50%)', fontFamily: "'JetBrains Mono', monospace",
                        textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                      <Receipt size={12} />Generate Invoice
                    </button>
                  ) : (
                    <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10,
                      color: 'hsl(142,70%,55%)', border: '1px solid hsl(142,50%,25%)',
                      padding: '4px 10px', background: 'hsl(142,30%,8%)' }}
                      data-testid="invoice-badge">
                      Invoice: {selected.invoice_no}
                    </span>
                  )}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1, marginBottom: 20 }}>
                {[
                  { label: 'Customer', val: selected.customer_name },
                  { label: 'Quantity', val: (selected.quantity || 0).toLocaleString() + ' units' },
                  { label: 'Invoice Amount', val: selected.invoice_amount ? `₹${(selected.invoice_amount/1000).toFixed(0)}K` : '—' },
                  { label: 'Scheduled', val: selected.scheduled_date ? new Date(selected.scheduled_date).toLocaleDateString('en-IN') : '—' },
                ].map(k => (
                  <div key={k.label} style={{ background: 'hsl(0,0%,6%)', border: '1px solid hsl(0,0%,12%)', padding: 14 }}>
                    <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'hsl(0,0%,40%)', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 4 }}>{k.label}</div>
                    <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: 'hsl(0,0%,80%)', fontWeight: 600 }}>{k.val || '—'}</div>
                  </div>
                ))}
              </div>

              {/* Transport */}
              <div style={{ background: 'hsl(0,0%,6%)', border: '1px solid hsl(0,0%,12%)', padding: 16, marginBottom: 16 }}>
                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'hsl(0,0%,40%)', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 10 }}>Transport Details</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                  {[
                    { label: 'Transporter', val: selected.transporter },
                    { label: 'Vehicle No', val: selected.vehicle_no },
                    { label: 'Invoice No', val: selected.invoice_no },
                    { label: 'Product', val: selected.product_name },
                  ].map(f => (
                    <div key={f.label}>
                      <div style={{ fontSize: 9, color: 'hsl(0,0%,40%)', fontFamily: "'JetBrains Mono', monospace", textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 2 }}>{f.label}</div>
                      <div style={{ fontSize: 11, color: 'hsl(0,0%,75%)', fontFamily: "'JetBrains Mono', monospace" }}>{f.val || '—'}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* e-Way Bill section */}
              <div style={{ background: selected.ewaybill_no ? 'hsl(142,30%,8%)' : 'hsl(0,0%,6%)',
                border: `1px solid ${selected.ewaybill_no ? 'hsl(142,50%,20%)' : 'hsl(0,0%,12%)'}`, padding: 16, marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: selected.ewaybill_no ? 'hsl(142,70%,50%)' : 'hsl(0,84%,60%)', textTransform: 'uppercase', letterSpacing: '0.12em' }}>
                    e-Way Bill {selected.ewaybill_no ? '— ATTACHED' : '— NOT ATTACHED'}
                  </div>
                  {!selected.ewaybill_no && (
                    <span style={{ fontSize: 9, background: 'hsl(0,60%,20%)', color: 'hsl(0,80%,60%)', padding: '2px 8px', fontFamily: "'JetBrains Mono', monospace", textTransform: 'uppercase' }}>REQUIRED FOR GST COMPLIANCE</span>
                  )}
                </div>
                {selected.ewaybill_no ? (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                    {[
                      { label: 'e-Way Bill No', val: selected.ewaybill_no },
                      { label: 'Bill Date', val: selected.ewaybill_date },
                      { label: 'Transporter ID', val: selected.transporter_id },
                      { label: 'Vehicle Type', val: selected.vehicle_type },
                      { label: 'Distance (km)', val: selected.distance_km },
                    ].map(f => (
                      <div key={f.label}>
                        <div style={{ fontSize: 9, color: 'hsl(0,0%,40%)', fontFamily: "'JetBrains Mono', monospace", textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 2 }}>{f.label}</div>
                        <div style={{ fontSize: 11, color: 'hsl(142,70%,55%)', fontFamily: "'JetBrains Mono', monospace", fontWeight: 600 }}>{f.val || '—'}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: 'hsl(0,0%,40%)' }}>
                    Click "Add e-Way Bill" to attach e-Way Bill number, transporter ID and vehicle details.
                  </div>
                )}
              </div>

              <div style={{ background: 'hsl(0,0%,6%)', border: '1px solid hsl(0,0%,12%)', padding: 16 }}>
                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'hsl(0,0%,40%)', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 10 }}>Document Checklist</div>
                <DocCheck ok={!!selected.invoice_no} label="Invoice generated" />
                <DocCheck ok={!!selected.ewaybill_no} label="e-Way Bill attached" />
                <DocCheck ok={!!selected.transporter} label="Transporter assigned" />
                <DocCheck ok={!!selected.vehicle_no} label="Vehicle assigned" />
                <DocCheck ok={selected.qc_released} label="QC batch released" />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* New Dispatch Modal */}
      {showAdd && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'hsl(0,0%,5%)', border: '1px solid hsl(0,0%,18%)', width: 520, padding: 28, maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
              <h3 style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 16, textTransform: 'uppercase', color: 'hsl(0,0%,95%)' }}>New Dispatch Note</h3>
              <button onClick={() => setShowAdd(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'hsl(0,0%,50%)' }}><X size={18} /></button>
            </div>
            <form onSubmit={handleAdd}>
              <DraftRestoreBanner hasDraft={hasDraft} onRestore={restoreDraft} onDiscard={discardDraft} />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={LBL}>Order No</label>
                  <SmartPicker
                    endpoint="/orders"
                    params={{ status: 'ready_to_dispatch' }}
                    labelKey="order_no"
                    searchKeys={['order_no', 'customer_name']}
                    placeholder="Search order..."
                    value={form.order_no}
                    onSelect={(order) => {
                      setForm(prev => ({
                        ...prev,
                        order_no: order.order_no,
                        customer_name: order.customer_name,
                        product_name: order.product_name || prev.product_name,
                        product_sku: order.product_sku || prev.product_sku,
                        quantity: String(order.quantity || prev.quantity),
                      }));
                    }}
                  />
                </div>
                <div>
                  <label style={LBL}>Carrier (Transporter)</label>
                  <SmartPicker
                    endpoint="/carriers"
                    params={{ status: 'active' }}
                    labelKey="name"
                    searchKeys={['name', 'code', 'gstin']}
                    placeholder="Search carrier..."
                    value={form.transporter}
                    onSelect={(carrier) => {
                      setForm(prev => ({
                        ...prev,
                        transporter: carrier.name,
                        carrier_id: carrier.id,
                      }));
                      if (carrier.transporter_id) {
                        setEwayForm(prev => ({ ...prev, transporter_id: carrier.transporter_id }));
                      }
                    }}
                    data-testid="dispatch-field-transporter"
                  />
                </div>
                {[
                  { key: 'customer_name', label: 'Customer Name', req: true },
                  { key: 'product_name', label: 'Product Name', req: true },
                  { key: 'product_sku', label: 'Product SKU (for stock)' },
                  { key: 'quantity', label: 'Quantity', req: true },
                  { key: 'scheduled_date', label: 'Scheduled Date', type: 'date', req: true },
                  { key: 'vehicle_no', label: 'Vehicle No' },
                  { key: 'invoice_no', label: 'Invoice No' },
                  { key: 'invoice_amount', label: 'Invoice Amount (₹)' },
                ].map(f => (
                  <div key={f.key}>
                    <label style={LBL}>{f.label}</label>
                    <input value={form[f.key]} required={f.req} type={f.type || 'text'}
                      data-testid={`dispatch-field-${f.key}`}
                      onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
                      style={INP} />
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 20, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setShowAdd(false)} className="btn-ghost">Cancel</button>
                <button type="submit" data-testid="save-dispatch-btn" className="btn-primary">CREATE</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* e-Way Bill Modal */}
      {showEway && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'hsl(0,0%,5%)', border: '1px solid hsl(197,100%,30%)', width: 480, padding: 28 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
              <div>
                <h3 style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 16, textTransform: 'uppercase', color: 'hsl(0,0%,95%)', margin: 0 }}>e-Way Bill Details</h3>
                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: 'hsl(0,0%,40%)', marginTop: 4 }}>{selected?.dispatch_no}</div>
              </div>
              <button onClick={() => setShowEway(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'hsl(0,0%,50%)' }}><X size={18} /></button>
            </div>
            <form onSubmit={handleEWaySubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div style={{ gridColumn: '1/-1' }}>
                  <label style={LBL}>e-Way Bill Number *</label>
                  <input value={ewayForm.ewaybill_no} required onChange={e => setEwayForm(p => ({ ...p, ewaybill_no: e.target.value }))} placeholder="e.g. 331012345678" style={INP} data-testid="eway-no" />
                </div>
                <div>
                  <label style={LBL}>Bill Date</label>
                  <input type="date" value={ewayForm.ewaybill_date} onChange={e => setEwayForm(p => ({ ...p, ewaybill_date: e.target.value }))} style={INP} data-testid="eway-date" />
                </div>
                <div>
                  <label style={LBL}>Distance (km)</label>
                  <input type="number" value={ewayForm.distance_km} onChange={e => setEwayForm(p => ({ ...p, distance_km: e.target.value }))} placeholder="e.g. 450" style={INP} data-testid="eway-distance" />
                </div>
                <div style={{ gridColumn: '1/-1' }}>
                  <label style={LBL}>Transporter GSTIN / ID</label>
                  <input value={ewayForm.transporter_id} onChange={e => setEwayForm(p => ({ ...p, transporter_id: e.target.value }))} placeholder="e.g. 27AAACR5055K1ZS" style={INP} data-testid="eway-transporter-id" />
                </div>
                <div style={{ gridColumn: '1/-1' }}>
                  <label style={LBL}>Vehicle Type</label>
                  <select value={ewayForm.vehicle_type} onChange={e => setEwayForm(p => ({ ...p, vehicle_type: e.target.value }))} style={{ ...INP }}>
                    {VEHICLE_TYPES.map(v => <option key={v} value={v}>{v}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ marginTop: 20, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setShowEway(false)} className="btn-ghost">Cancel</button>
                <button type="submit" data-testid="save-eway-btn" className="btn-primary">SAVE e-WAY BILL</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
